import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ContentState, 
  // convertFromHTML, 
  convertFromRaw, 
  convertToRaw, 
  EditorState,
  RichUtils, Modifier
} from 'draft-js';
import Editor, { composeDecorators } from '@draft-js-plugins/editor';
import createMentionPlugin from '@draft-js-plugins/mention';
import '@draft-js-plugins/mention/lib/plugin.css';
import {
  DraftHandle,
  ENTITY_MUTABILITY, SEARCH_MEMBERS_LIMIT
} from './domain';
import { GroupService } from 'api/services/groups';
import { IPostableDraftProps } from './IPostableDraftProps';
import _ from 'lodash';
import { stateToHTML } from 'draft-js-export-html';
import './PostableDraft.scss';
import createToolbarPlugin, {
  Separator,
} from '@draft-js-plugins/static-toolbar';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  UnorderedListButton,
  OrderedListButton,
} from '@draft-js-plugins/buttons';
import '@draft-js-plugins/static-toolbar/lib/plugin.css';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import '@draft-js-plugins/linkify/lib/plugin.css';
import HeadlinesButton from './HeadlinesButton';
import createLinkPlugin from '@draft-js-plugins/anchor';
import '@draft-js-plugins/anchor/lib/plugin.css';
import createImagePlugin from '@draft-js-plugins/image';
import '@draft-js-plugins/image/lib/plugin.css';
import createAlignmentPlugin from '@draft-js-plugins/alignment';
import createFocusPlugin from '@draft-js-plugins/focus';
import MentionComponent from 'components/PostEditor/MentionComponent';
import EntryComponent from 'components/PostEditor/EntryComponent';
import { EntryComponentProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/Entry/Entry';
import 'draft-js/dist/Draft.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-regular-svg-icons';
import ImageCropper from 'elements/imageCropper';
import miscUtils from 'utils/miscUtils';
import { toast } from 'react-toastify';
import ImageButton from './ImageButton';
import clsx from "clsx";
import GIFButton from './GIFButton';
import GIF from 'components/GIF';
import styles from './postdraft.module.scss'
import { WorkflowMergeTags } from 'containers/Workflows/WorkflowCreation2/WorkflowCreation2';
import CommunityMergeTags from 'components/CommunityMergeTags/CommunityMergeTags';
import { convertFromHTML } from 'draft-convert';
import { AppContext } from 'containers/main/MainComponent';

// user mentions plugin
const userMentionPlugin = createMentionPlugin({
  entityMutability: ENTITY_MUTABILITY.IMMUTABLE,
  //@ts-ignore
  mentionComponent: MentionComponent,
  theme: {
    mention: 'postable-draft-mention-text',
    mentionSuggestions: 'postable-draft-mention-suggestions-container',
  },
  mentionPrefix: '@',
  supportWhitespace: true,
});

// channel mentions plugin
const channelMentionPlugin = createMentionPlugin({
  entityMutability: ENTITY_MUTABILITY.IMMUTABLE,
  //@ts-ignore
  mentionComponent: MentionComponent,
  theme: {
    mention: 'postable-draft-mention-channel',
    mentionSuggestions: 'postable-draft-mention-suggestions-container',
  },
  mentionPrefix: '#',
  mentionTrigger: '#',
  supportWhitespace: true,
});
const { MentionSuggestions: UserMentionSuggestions } = userMentionPlugin;
const { MentionSuggestions: ChannelMentionSuggestions } = channelMentionPlugin;

//linkify plugin
const linkifyPlugin = createLinkifyPlugin({
  theme: {
    link: 'postable-draft-linkify-text',
  }
});

//anchor plugin
const linkPlugin = createLinkPlugin({
  theme: {
    input: `postable-draft-link-input`,
    inputInvalid: 'postable-draft-link-input--invalid',
    link: 'postable-draft-link-text'
  }
});
const { LinkButton } = linkPlugin

//focus and alignment
const focusPlugin = createFocusPlugin();
const alignmentPlugin = createAlignmentPlugin();

const imageDecorators: any = composeDecorators(
  alignmentPlugin.decorator,
  focusPlugin.decorator,
);

//image plugin
//@ts-ignore
const imagePlugin = createImagePlugin({ imageDecorators });

const PostableDraft: React.FC<IPostableDraftProps> = (props): React.ReactElement => {

  const { memoizedPlugins, Toolbar } = React.useMemo(() => {
    const staticToolbarPlugin = createToolbarPlugin();
    return {
      memoizedPlugins: [staticToolbarPlugin],
      Toolbar: staticToolbarPlugin.Toolbar
    };
  }, []);

  const plugins = [...memoizedPlugins, userMentionPlugin, channelMentionPlugin, linkifyPlugin, linkPlugin, focusPlugin, alignmentPlugin, imagePlugin];

  const { placeholder = "Write a post", acceptTitle = true, acceptCoverImage = true } = props;

  const ref = React.useRef<Editor>(null);
  const inlineImageRef = React.useRef<any>(null);

  const [gifLoading, setGIFLoading] = useState(false)
  const [editorState, setEditorState] = React.useState<EditorState>(() => {
    if (props.editMode && props.draftJSON) {
      // const contentState: ContentState = convertFromRaw(JSON.parse(props.draftJSON));
      // return EditorState.createWithContent(contentState);

      try {
        const contentState: ContentState = convertFromRaw(JSON.parse(props.draftJSON));
        return EditorState.createWithContent(contentState);
      } catch (error) {
        console.warn('Invalid draftJSON, falling back to postDescription:', error);
        // Fall through to HTML conversion using postDescription
      }
    }

    if (props.editMode && (!props.draftJSON || props.postDescription)) {
      const contentState: ContentState = convertFromHTML({
        htmlToBlock: (nodeName: any, node: any) => {
          if (nodeName === 'img') {
            return {
              type: 'atomic',
              data: {
                src: node.getAttribute('src'),
                alt: node.getAttribute('alt'),
              },
            };
          }
          if (nodeName === 'p') {
            return {
              type: 'unstyled',
            };
          }
          if (nodeName === 'blockquote') {
            return {
              type: 'blockquote',
            };
          }
          return undefined; // Default behavior for other block types
        },
        htmlToStyle: (nodeName: any, node: any, currentStyle: any) => {
          if (nodeName === 'b' || nodeName === 'strong') {
            return currentStyle.add('BOLD');
          }
          if (nodeName === 'i' || nodeName === 'em') {
            return currentStyle.add('ITALIC');
          }
          if (nodeName === 'u') {
            return currentStyle.add('UNDERLINE');
          }
          if (nodeName === 'code') {
            return currentStyle.add('CODE');
          }
          return currentStyle;
        },
        htmlToEntity: (nodeName: any, node: any, createEntity: any) => {
          if (nodeName === 'a') {
            return createEntity('LINK', 'MUTABLE', {
              url: node.getAttribute('href'),
            });
          }
          if (nodeName === 'img') {
            return createEntity('IMAGE', 'IMMUTABLE', {
              src: node.getAttribute('src'),
              alt: node.getAttribute('alt'),
            });
          }
        },
      })(props.postDescription || "");
      return EditorState.createWithContent(contentState);
    }
    
    return EditorState.createEmpty();
  }
  );
  const [open, setOpen] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [disableEditor, setDisableEditor] = React.useState<boolean>(true);
  const [isCoverImageCropperOpen, setIsCoverImageCropperOpen] = React.useState<boolean>(false);
  const [showCoverImageActions, setShowCoverImageActions] = React.useState<boolean>(false);
  const [showGIFComp, setShowGIFComp] = useState<boolean>(false);

  //channel mentions
  const [isChannelMentionsOpen, setIsChannelMentionsOpen] = React.useState(false);
  const [channelMentions, setChannelMentions] = React.useState([]);

  React.useEffect(() => {
    props.resetDraftRef.current = () => setEditorState(EditorState.createEmpty());
    focusTitleInput();
    if(ref.current) {
      // ref.current.focus();
      ref.current.blur();
      setTimeout(() => {
        if(ref.current) {
          ref.current.blur();
        }
      }, 100);
    }
  }, []);

  React.useEffect(() => {
    if (props.postCoverImage) {
      const img = new Image();
      img.src = props.postCoverImage.url ? props.postCoverImage.url : URL.createObjectURL(props.postCoverImage);

      img.onload = () => {
        if (
          !props.coverImageMaxWidth &&
          !props.coverImageMaxHeight &&
          (img.height < 250 || img.width < 695)
        ) {
          props.setPostCoverImage && props.setPostCoverImage(null);
          setIsCoverImageCropperOpen(false);
          toast("Minimum required size for post cover image is 695x254.", {
            type: 'error',
            autoClose: 3000,
            position: "top-center",
            hideProgressBar: true,
            theme: "light"
          });
        }
      };
    }
  }, [props.postCoverImage]);

  React.useEffect(() => {
    if (!props.acceptTitle) {
      setDisableEditor(false);
    }
  }, [props.acceptTitle])

  const onChange = React.useCallback((newState: EditorState, addImage?: boolean) => {
    if(props.onEditorChange) {
      props.onEditorChange(newState);
    }
    let finalState = newState;

    if (inlineImageRef.current && addImage) {
      finalState = imagePlugin.addImage(newState, inlineImageRef.current, {});
    }
    setEditorState(finalState);
    const contentState: ContentState = finalState.getCurrentContent();
    const rawState = convertToRaw(contentState);
    props.onStateChange(getHtmlState(contentState), JSON.stringify(rawState));
  }, [inlineImageRef, imagePlugin, props.onStateChange, setEditorState]);

  const handleDraftJsTagClick = (tag: string, value: any) => {
    console.log('handleDraftJsTagClick', tag)
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    // Check if the selection is collapsed (insertion point)
    if (!selectionState.isCollapsed()) {
      console.error('The selection must be collapsed to insert a tag.');
      return;
    }

    // Insert the tag at the current cursor position
    const contentStateWithEntity = Modifier.insertText(
      contentState,
      selectionState,
      tag
    );

    // Update the editor state with the new content
    const newEditorState = EditorState.push(
      editorState,
      contentStateWithEntity,
      'insert-characters'
    );

    // Move the cursor to the end of the inserted tag
    const newSelection: any = selectionState.merge({
      anchorOffset: selectionState.getAnchorOffset() + tag.length,
      focusOffset: selectionState.getAnchorOffset() + tag.length,
    });

    const updatedEditorState = EditorState.forceSelection(
      newEditorState,
      newSelection
    );

    if (props.updateworkflowMergeTags && props.workflowMergeTags) {
      props.updateworkflowMergeTags(tag, value)
    }

    setEditorState(updatedEditorState);
  };

  const getHtmlState = (contentState: ContentState) => {
    // eslint-disable-next-line
    const options = {
      // eslint-disable-next-line consistent-return
      entityStyleFn: (entity: any) => {
        const entityType = entity.get("type").toLowerCase();
        if (entityType === "mention") {
          const data = entity.getData();
          return {
            element: "span",
            attributes: {
              "data-mention-id": _.get(data, "mention.id"),
              class: "mentioned-member"
            },
            style: {
              // Put styles here...
            }
          };
        }
      }
    };
    return stateToHTML(contentState, options);
  }

  const onOpenChange = React.useCallback((_open: boolean) => {
    setOpen(_open);
  }, []);

  // channel mentions
  const onChannelMentionsOpen = React.useCallback((arg0: boolean) => {
    setIsChannelMentionsOpen(arg0);
  }, []);
  const onChannelsSearchChange = React.useCallback(({ value }: { value: string }) => {
    seachForChannels(value);
  }, []);

  const onSearchChange = React.useCallback(({ value }: { value: string }) => {
    searchForMembers(value);
  }, []);

  const searchForMembers = (value: string) => {
    GroupService.getInstance()
      .getGroupMembersInMention(props.group.id, value, SEARCH_MEMBERS_LIMIT)
      .then((response: any) => {
        setSuggestions(response.data.map((groupMember: any) => {
          return {
            name: groupMember.user.full_name,
            avatar: groupMember.user.profile_image,
            id: `${groupMember.user.id}`,
          }
        }));
      }).catch((error: any) => {
        console.error(error)
        new Error(error)
      })
  }

        const { groupChannels } =  React.useContext(AppContext);
  
  const seachForChannels = (query: string) => {
    // const groupChannels = (props.group && props.group.channels) ? props.group.channels : []
     if (groupChannels) {
    const filteredChannels =groupChannels && groupChannels.filter((channel: any) =>
      channel.name.toLowerCase().includes(query.toLowerCase())
    );
    setChannelMentions(filteredChannels.map((channel: any) => {
      return {
        name: channel.name,
        avatar: channel.emoji,
        id: channel.id,
        slug: channel.slug,
      }
    }));
  }
  }

  const handleKeyCommand = (command: any) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      setEditorState(newState);
      return DraftHandle.HANDLED;
    }

    return DraftHandle.NOTHANDLED;
  }

  const onAddMention = (mention: any) => {
    props.onAddMention(mention.id);
  }

  const focusTitleInput = () => {
    const titleInput: HTMLElement = document.getElementById(
      "postable-draft-editor-title-input") as HTMLElement;

    if (titleInput) {
      titleInput.focus();
    }
  }

  const cropCoverImage = (crop: any) => {
    props.setPostCoverImage(crop.file);
    setIsCoverImageCropperOpen(false);
  }

  const cancelCoverImageCrop = () => {
    props.setPostCoverImage(null);
    setIsCoverImageCropperOpen(false);
  }

  const pickCoverImage = async (event: any) => {
    if (event.target.files) {
      if (!miscUtils.validatePhotoFileSize(event.target.files[0])) {
        return false;
      }

      if (!miscUtils.validateFileName(event.target.files[0])) {
        return;
      }
      if (!miscUtils.validateSpecialChars(event.target.files[0])) {
        return false;
      }

      const file = event.target.files[0]
      if (file.name.endsWith(".heic") || file.name.endsWith(".HEIC")) {
        const convertedBlob: any = await miscUtils.convertHeicToJpeg(file);
        if (convertedBlob) {
          const jpgFileName = file.name.replace(/\.heic$/i, '.jpg');
          const convertedFile = new File([convertedBlob], jpgFileName, { type: 'image/jpeg' });
          props.setPostCoverImage(convertedFile);

          const img = new Image(undefined, undefined);
          img.src = URL.createObjectURL(convertedFile);

          img.onload = () => {
            if ((img.height >= 250 && img.width >= 695) || props.coverImageAspectRatio) {
              setIsCoverImageCropperOpen(true);
            }
          };
        }
      } else {
        props.setPostCoverImage(event.target.files[0]);
        const img = new Image();
        img.src = URL.createObjectURL(event.target.files[0]);

        img.onload = () => {
          if ((img.height >= 250 && img.width >= 695) || props.coverImageAspectRatio) {
            setIsCoverImageCropperOpen(true);
          }
        };
      }
    }
  }


  const pickInlineImage = async (event: any) => {
    if (event.target.files) {
      const file: File = event.target.files[0];

      if (!miscUtils.validateFileName(file)) {
        return false;
      }

      if (!miscUtils.validateSpecialChars(file)) {
        return false;
      }

      if (!miscUtils.validatePhotoFileSize(file)) {
        return false;
      }

      const inlineImageFormData = new FormData();
      inlineImageFormData.append("image", file);

      inlineImageFormData.append("category", "posts");
      GroupService.getInstance()
        .uploadTextEditorImage(props.group.id, inlineImageFormData)
        .then((response: any) => {
          inlineImageRef.current = response.file.url;
          onChange(editorState, true);
        }).catch((error: any) => {
          console.error(error)
          new Error(error)
        })
    }
  }

  const clickCoverImageInput = () => {
    const coverImageInputElement: HTMLElement = document.getElementById(`postable-draft-editor-cover-input-${placeholder}`) as HTMLElement;

    if (coverImageInputElement) {
      coverImageInputElement.click();
    }
  }

  const clickInlineImageInput = () => {
    const inlineImageInputElement: HTMLElement = document.getElementById(getFileInputId()) as HTMLElement;

    if (inlineImageInputElement) {
      inlineImageInputElement.click();
    }
  }


  const clickInlineGif = async (gif: any) => {
    await fetch(gif && gif.images.original.url)
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], gif.title, { type: "image/gif" });

        if (file === null || !file || gif === null) return

        const inlineImageFormData = new FormData();
        inlineImageFormData.append("image", file);
        inlineImageFormData.append("category", "posts");

        setGIFLoading(true);
        GroupService.getInstance()
          .uploadTextEditorImage(props.group.id, inlineImageFormData)
          .then((response: any) => {
            inlineImageRef.current = response.file.url;
            onChange(editorState, true);
          })
          .catch(error => {
            console.error(error)
            new Error(error)
          })
          .finally(() => {
            setGIFLoading(false);
          });

      })
      .catch(error => {
        console.error(error)
        new Error(error)
      });
  }

  const getFileInputId = () => {
    if(props.instanceIdentifier) {
      return `postable-draft-editor-inline-image-input-${props.instanceIdentifier}`;
    }

    return `postable-draft-editor-inline-image-input-${placeholder}`;
  }

  return (
    <div className={clsx(
      `postable-draft-container ${props.fullScreen ?
        "postable-draft-container--fullscreen" : ""} ${props.readOnly ?
          "postable-draft-container--readonly" : ""}`,
      props.containerClasses
    )}>
      {!props.readOnly && (
        <Toolbar>
          {
            //@ts-ignore
            (externalProps) => (
              <>
                <HeadlinesButton {...externalProps} />
                <BoldButton {...externalProps} />
                <ItalicButton {...externalProps} />
                <UnderlineButton {...externalProps} />

                {/*
                 // @ts-ignore */}
                <Separator {...externalProps} />
                <UnorderedListButton {...externalProps} />
                <OrderedListButton {...externalProps} />

                {/*
                  // @ts-ignore */}
                <Separator {...externalProps} />

                <LinkButton {...externalProps} />
                {!miscUtils.groupStorageLimitIsMaxedOut(props.group) ? (
                  <>
                  <ImageButton onClick={clickInlineImageInput} />
                  <GIFButton onClick={() => setShowGIFComp(!showGIFComp)} />
                  </>
                ) : <></>}

                {props.workflowMergeTags && props.workflowMergeTags.length > 0 &&
                  <WorkflowMergeTags
                    eventAndActionsMergeTags={props.workflowMergeTags}
                    handleTagClick={handleDraftJsTagClick}
                    menuIconStyles={styles.menuIcon}
                  />
                }
                {
                  props.communityMergeTags && props.communityMergeTags.length > 0 &&
                  <CommunityMergeTags
                    mergeTags={props.communityMergeTags}
                    handleTagClick={handleDraftJsTagClick}
                    menuIconStyles={styles.menuIcon2}
                  />
                }
              </>
            )
          }
        </Toolbar>
      )}

      <div
        className={clsx(
          `postable-draft-editor-container ${props.fullScreen ?
            "postable-draft-editor-container--fullscreen" : ""}`,
          props.editorContainerClasses
        )}>
        {ReactDOM.createPortal(
          <>
            <div
              className="postable-draft-editor-cover-portal-container"
            >
              <ImageCropper
                aspect={props.coverImageAspectRatio ? props.coverImageAspectRatio : 695 / 254}
                maxWidth={props.coverImageMaxWidth ? props.coverImageMaxWidth : 695}
                maxHeight={props.coverImageMaxHeight ? props.coverImageMaxHeight : 254}
                sourceImage={props.postCoverImage ? props.postCoverImage.url ? props.postCoverImage.url : URL.createObjectURL(props.postCoverImage) : null}
                isOpen={isCoverImageCropperOpen}
                onCropCancel={cancelCoverImageCrop}
                onCropFinished={cropCoverImage}
                imageSrc={props.postCoverImage && props.postCoverImage}
              />
            </div>
          </>,
          //@ts-ignore
          document.getElementById('gp-portal-root')
        )}
        {acceptCoverImage && !props.hideCoverImageInput ? (
          <>
            {props.postCoverImage ? (
              <>
                <div
                  className={`postable-draft-editor-cover-image-container${props.coverImageAspectRatio ? ' custom-dimensions' : ''}`}
                  onMouseOver={() => setShowCoverImageActions(true)}
                  onMouseOut={() => setShowCoverImageActions(false)}
                >
                  <img
                    className={`postable-draft-editor-cover-image-image${props.coverImageAspectRatio ? ' custom-dimensions' : ''}`}
                    src={props.postCoverImage.url ? props.postCoverImage.url : URL.createObjectURL(props.postCoverImage)}
                  />
                  <div
                    className={`postable-draft-editor-cover-image-action-container ${!showCoverImageActions ? 'hidden' : ''}`}
                  >
                    {!miscUtils.groupStorageLimitIsMaxedOut(props.group) ? (
                      <button
                        className="postable-draft-editor-cover-image-action-button"
                        onClick={clickCoverImageInput}
                      >
                        Update
                      </button>
                    ) : <></>}
                    <button
                      className={`postable-draft-editor-cover-image-action-button${!miscUtils.groupStorageLimitIsMaxedOut(props.group) ? ' spaced' : ''}`}
                      onClick={() => props.setPostCoverImage(null)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {!miscUtils.groupStorageLimitIsMaxedOut(props.group) ? (
                  <div
                  className={`postable-draft-editor-cover-container`}
                  onClick={clickCoverImageInput}>
                    <span className="postable-draft-editor-cover-icon">
                      <FontAwesomeIcon icon={faImage} />
                    </span>
                    <p className="postable-draft-editor-cover-label">
                      Add cover image
                    </p>
                  </div>
                ) : <></>}
              </>
            )}
          </>
        ) : (<></>)}

        <input
          type="file"
          hidden
          id={`postable-draft-editor-cover-input-${placeholder}`}
          onChange={pickCoverImage}
          accept="image/png, image/jpeg, .heic"
          value=""
        />
        <input
          type="file"
          hidden
          id={getFileInputId()}
          onChange={pickInlineImage}
          accept="image/png, image/jpeg, image/gif, .heic"
          value=""
        />

        {acceptTitle && !props.hideTitleInput ? (
          <input
            type="text"
            id="postable-draft-editor-title-input"
            className="postable-draft-editor-title-input"
            value={props.title}
            onChange={(e: any) => props.setTitle(e.target.value)}
            placeholder={props.titlePlaceholder ? props.titlePlaceholder : `Enter title ${props.requireTitle ? '(required)' : '(optional)'}`}
            onFocus={() => setDisableEditor(true)}
            onBlur={() => setDisableEditor(false)}
            maxLength={255}
          />
        ) : (<></>)}

        <div onClick={() => { ref.current!.focus() }}>
          <Editor
            editorState={editorState}
            onChange={onChange}
            plugins={plugins}
            ref={ref}
            handleKeyCommand={handleKeyCommand}
            placeholder={props.editorPlaceholder ? props.editorPlaceholder : placeholder}
            readOnly={props.readOnly || disableEditor}
            spellCheck={true}
          />
          {
            gifLoading &&
            <span
                style={{
                  color: "#9FA1A1",
                  fontSize: "16px",
                  display: 'flex',
                  justifyContent: 'center',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  alignItems: 'flex-start'
                }}
              >
              Loading GIF content...
            </span>
          }
        </div>

        <div 
          className={`${window.location.pathname.includes('members')
            ?
            "invite-member-page-post-GIF-container"
            :
            "post-GIF-container"} ${props.gifContainerClass}`}
        >
        {showGIFComp &&
          <GIF
            clickInlineGif={clickInlineGif}
            setShowGIFComp={setShowGIFComp}
            fullScreen={props.fullScreen}
            gifGridClass={props.gifGridClass}
          />
        }
        </div>

      </div>
      {
        !props.disableTaggingonAutomations &&
        <UserMentionSuggestions
        open={open}
        onOpenChange={onOpenChange}
        suggestions={suggestions}
        onSearchChange={onSearchChange}
        onAddMention={onAddMention}
        entryComponent={(EntryComponent as unknown as React.ComponentType<EntryComponentProps>)}
      />
      }

      <ChannelMentionSuggestions
        open={isChannelMentionsOpen}
        onOpenChange={onChannelMentionsOpen}
        suggestions={channelMentions}
        onSearchChange={onChannelsSearchChange}
        onAddMention={onAddMention}
        entryComponent={(EntryComponent as unknown as React.ComponentType<EntryComponentProps>)}
      />
    </div>
  );
}

export default PostableDraft;
